import { useState } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Trash2, MessageSquare } from 'lucide-react';
import { useAgentMemory } from '@/hooks/useAgentMemory';

function fmtDate(ts: number) {
  try { return new Date(ts).toLocaleString(); } catch { return ''; }
}

function summarize(text: string, max = 80) {
  const t = text.replace(/\s+/g, ' ').trim();
  return t.length > max ? t.slice(0, max - 1) + '…' : t;
}

export function ConversationHistory() {
  const { conversations, clearHistory } = useAgentMemory();
  const [openItem, setOpenItem] = useState<string | undefined>(undefined);

  const recent = [...conversations]
    .sort((a, b) => b.createdAt - a.createdAt)
    .slice(0, 5);

  if (recent.length === 0) {
    return (
      <div className="flex flex-col items-center text-center gap-2 py-10 px-4 text-muted-foreground">
        <MessageSquare className="h-6 w-6 opacity-60" />
        <div className="text-sm">No hay conversaciones aún.</div>
        <div className="text-xs">Tu historial aparecerá aquí.</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-3 py-2 border-b">
        <div className="text-xs text-muted-foreground">
          {recent.length} conversación{recent.length !== 1 ? 'es' : ''} recientes
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="ghost" size="sm" className="h-7 gap-1.5 text-xs text-destructive">
              <Trash2 className="h-3.5 w-3.5" /> Limpiar
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Limpiar historial</AlertDialogTitle>
              <AlertDialogDescription>
                Se eliminarán todas las conversaciones almacenadas para esta organización. Esta acción no se puede deshacer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={() => clearHistory()}>Eliminar</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <ScrollArea className="flex-1 min-h-0">
        <Accordion type="single" collapsible value={openItem} onValueChange={setOpenItem} className="px-2 py-2">
          {recent.map((c) => {
            const first = c.messages[0];
            const summary = first ? summarize(first.content) : 'Sin mensajes';
            return (
              <AccordionItem key={c.id} value={c.id} className="border-b last:border-b-0">
                <AccordionTrigger className="hover:no-underline py-2">
                  <div className="flex flex-col items-start gap-0.5 text-left flex-1 min-w-0 pr-2">
                    <div className="flex items-center gap-2 w-full">
                      <span className="text-[10px] text-muted-foreground">{fmtDate(c.createdAt)}</span>
                      <Badge variant="outline" className="ml-auto h-4 px-1.5 text-[10px]">
                        {c.messages.length} turnos
                      </Badge>
                    </div>
                    <div className="text-xs text-foreground/90 truncate w-full">{summary}</div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-1 pb-2">
                  <div className="space-y-1.5">
                    {c.messages.slice(0, 12).map((m, i) => (
                      <div
                        key={i}
                        className={`text-[11px] rounded p-1.5 border ${
                          m.role === 'user' ? 'bg-primary/5' : m.role === 'assistant' ? 'bg-card' : 'bg-muted'
                        }`}
                      >
                        <span className="font-semibold uppercase text-[9px] text-muted-foreground mr-1">
                          {m.role}:
                        </span>
                        <span className="whitespace-pre-wrap break-words">{summarize(m.content, 200)}</span>
                      </div>
                    ))}
                    {c.messages.length > 12 && (
                      <div className="text-[10px] text-muted-foreground text-center">
                        +{c.messages.length - 12} mensajes más
                      </div>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </ScrollArea>
    </div>
  );
}
